import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sprout } from "lucide-react";

export default function AuthPage({ mode = "login" }) {
  const isSignup = mode === "signup";
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    login(email, isSignup ? name : undefined);
    toast.success(isSignup ? "Welcome to NeighborNet!" : "Welcome back!");
    navigate("/dashboard");
  };

  return (
    <div className="min-h-[calc(100vh-80px)] noise-bg flex items-center py-12 px-4">
      <div className="max-w-5xl mx-auto w-full grid lg:grid-cols-2 gap-10 items-center">
        <div className="hidden lg:block">
          <div className="inline-flex items-center gap-2 rounded-2xl bg-primary text-primary-foreground p-2 mb-6">
            <Sprout size={20} />
          </div>
          <h1 className="font-display text-5xl font-bold tracking-tight leading-tight text-balance">
            {isSignup ? "Start giving. Start receiving." : "Welcome back home."}
          </h1>
          <p className="mt-4 text-muted-foreground text-lg max-w-md leading-relaxed">
            {isSignup
              ? "Create your NeighborNet account and get matched with people around you who need — or can give — a hand."
              : "Log back in and reconnect with your neighborhood, mentors and volunteer causes."}
          </p>
          <img
            src="https://images.unsplash.com/photo-1517456793572-1d8efd6dc135"
            alt="Community"
            className="mt-10 w-full h-64 object-cover rounded-3xl shadow-md"
          />
        </div>

        <div className="bg-white border border-border/60 rounded-3xl p-8 sm:p-10 shadow-sm fade-up">
          <h2 className="font-display text-3xl font-bold tracking-tight text-foreground">
            {isSignup ? "Create account" : "Log in"}
          </h2>
          <p className="text-sm text-muted-foreground mt-2 mb-8">
            {isSignup ? "Free forever. No credit card." : "Enter your email to continue."}
          </p>

          <form onSubmit={submit} className="space-y-5">
            {isSignup && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium">Full name</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace"
                  className="mt-2 h-12 rounded-xl border-border"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@neighborhood.org"
                className="mt-2 h-12 rounded-xl border-border"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 rounded-xl border-border"
              />
            </div>

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-sm hover:-translate-y-0.5 transition-all"
            >
              {isSignup ? "Create account" : "Log in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-center text-muted-foreground">
            {isSignup ? (
              <>Already a member?{" "}
                <Link to="/login" className="text-primary font-medium hover:underline" data-testid="switch-to-login">Log in</Link>
              </>
            ) : (
              <>New here?{" "}
                <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="switch-to-signup">Create an account</Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
